// Utility functions for session schedule components

import { BetterstackLink } from "./BetterstackLinksSection";

export const getSessionStatusColor = (status: string): string => {
  switch (status.toLowerCase()) {
    case "creating":
      return "#f39c12"; // Orange - session being created
    case "ready_to_configure":
      return "#e67e22"; // Dark orange - ready for configuration
    case "configuring":
      return "#3498db"; // Light blue - being configured
    case "ready_to_start":
      return "#2980b9"; // Blue - ready to begin
    case "in_progress":
      return "#27ae60"; // Green - actively running
    case "finished":
      return "#16a085"; // Teal - completed successfully
    case "finalized":
      return "#8e44ad"; // Purple - fully processed
    // Legacy/fallback statuses
    case "running":
      return "#27ae60"; // Green
    case "completed":
      return "#16a085"; // Teal
    case "error":
    case "failed":
      return "#c0392b"; // Red
    case "stopped":
    case "terminated":
      return "#7f8c8d"; // Gray
    case "initializing":
    case "starting":
      return "#3498db"; // Light blue
    case "pending":
    case "queued":
      return "#f39c12"; // Orange
    default:
      return "#95a5a6"; // Default gray for unknown statuses
  }
};

export const truncateEmail = (email: string | null, maxLength: number = 20): string => {
  if (!email) return "";
  if (email.length <= maxLength) return email;

  const [localPart, domain] = email.split("@");
  if (!domain) return email.slice(0, maxLength - 3) + "...";

  // Prioritize showing more of the local part (username)
  const domainWithAt = "@" + domain;
  const availableForLocal = maxLength - domainWithAt.length - 3; // -3 for "..."

  if (availableForLocal <= 2) {
    // If domain is too long, just truncate the whole email
    return email.slice(0, maxLength - 3) + "...";
  }

  return localPart.slice(0, availableForLocal) + "..." + domainWithAt;
};

/**
 * Generate Betterstack log links for debugging a session.
 * Creates links to various log sources filtered by relevant IDs and time range.
 *
 * Aligned with backend implementation in libs/admin/session_diagnostics.py
 */
export const generateBetterstackLinks = (
  sessionId: string,
  conversationId: string | null,
  stateHistory: { entered_at: string; data?: { scheduling_loop_id?: string; status?: string } }[] | null,
  logSessionId: string | null
): BetterstackLink[] => {
  const links: BetterstackLink[] = [];

  if (!stateHistory || stateHistory.length === 0) return links;

  const startTime = new Date(stateHistory[0].entered_at);
  const lastState = stateHistory[stateHistory.length - 1];
  const endTime = new Date(lastState.entered_at);

  // Convert to microseconds and add 5-minute buffer
  const bufferUs = 5 * 60 * 1_000_000;
  const startUs = startTime.getTime() * 1000;
  const endUs = endTime.getTime() * 1000;
  const rf = startUs - bufferUs;
  const rt = endUs + bufferUs;
  const anchor = Math.floor((startUs + endUs) / 2);

  const baseUrl = "https://telemetry.betterstack.com/team/t132769/tail";

  // Extract scheduling_loop_id from state history
  let schedulingLoopId: string | null = null;
  for (const state of stateHistory) {
    if (state.data?.scheduling_loop_id) {
      schedulingLoopId = state.data.scheduling_loop_id;
      if (state.data?.status === "finished") break;
    }
  }

  // UnrealClient logs (source 1180242) - uses stream_session_id
  // Use log_session_id for phantom sessions, otherwise use sessionId
  const isPhantomSession = logSessionId && logSessionId !== sessionId;
  const unrealStreamSessionId = isPhantomSession ? logSessionId : sessionId;
  const unrealQuery = encodeURIComponent(`stream_session_id="${unrealStreamSessionId}"`);
  links.push({
    label: "UnrealClient",
    url: `${baseUrl}?s=1180242&rf=${rf}&rt=${rt}&q=${unrealQuery}&a=${anchor}&pt=overview`,
    source_id: "1180242",
    description:
      "Unreal Engine client logs filtered by stream_session_id" +
      (isPhantomSession ? " (phantom session)" : ""),
  });

  // Pixelstreaming SS logs (source 1564822) - uses session_id (or log_session_id if phantom)
  const psQuery = isPhantomSession
    ? encodeURIComponent(`"${sessionId}" OR "${logSessionId}"`)
    : encodeURIComponent(`"${sessionId}"`);
  links.push({
    label: "Pixelstreaming SS",
    url: `${baseUrl}?s=1564822&rf=${rf}&rt=${rt}&q=${psQuery}&a=${anchor}`,
    source_id: "1564822",
    description:
      "Pixelstreaming signalling server logs filtered by session_id" +
      (isPhantomSession ? " (includes phantom session)" : ""),
  });

  // Puppet logs (source 701201) - uses conversation_id
  if (conversationId) {
    links.push({
      label: "Puppet",
      url: `${baseUrl}?s=701201&rf=${rf}&rt=${rt}&q=${conversationId}&a=${anchor}`,
      source_id: "701201",
      description: "Puppet service logs filtered by conversation_id",
    });
  }

  // Pixels Scheduling logs (source 1259458) - uses scheduling_loop_id
  if (schedulingLoopId) {
    links.push({
      label: "Pixels Scheduling",
      url: `${baseUrl}?s=1259458&rf=${rf}&rt=${rt}&q=${schedulingLoopId}&a=${anchor}`,
      source_id: "1259458",
      description: "Pixels scheduling service logs filtered by scheduling_loop_id",
    });
  }

  // Pixelstreaming Node Admin logs (source 1262533) - filters out healthz spam
  links.push({
    label: "Pixelstreaming Node Admin",
    url: `${baseUrl}?s=1262533&rf=${rf}&rt=${rt}&a=${anchor}&pt=overview&q=message!%3D%22%5Bhealthz%5D%20healthy%22%20_pattern!%3D79db8f25440e05336e45a51ea6cc8ff1`,
    source_id: "1262533",
    description: "Pixelstreaming node admin logs (healthz messages filtered)",
  });

  return links;
};
